import { useEffect, useState } from 'react';
import environment from '../environments/index';

// Declarar tipos para Google Maps
declare global {
  interface Window {
    google: any;
  }
}

interface UseGoogleMapsReturn {
  isLoaded: boolean;
  loadError: string | null;
}

export const useGoogleMaps = (): UseGoogleMapsReturn => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    const apiKey = environment.googleMaps.apiKey;
    
    if (!apiKey) {
      setLoadError('Google Maps API key no configurada');
      return;
    }

    // Verificar si Google Maps ya está cargado
    if (window.google && window.google.maps && window.google.maps.places) {
      console.log('✅ Google Maps ya está cargado');
      setIsLoaded(true);
      return;
    }
    
    // Crear script dinámicamente
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places&loading=async`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      // Verificar periódicamente si Google Maps está completamente cargado
      const checkGoogleMaps = () => {
        if (window.google && window.google.maps && window.google.maps.places) {
          setIsLoaded(true);
          setLoadError(null);
          return true;
        }
        return false;
      };

      // Verificar inmediatamente
      if (checkGoogleMaps()) {
        return;
      }

      // Si no está listo, verificar cada 100ms hasta 2 segundos
      let attempts = 0;
      const maxAttempts = 20; // 2 segundos
      
      intervalId = setInterval(() => {
        attempts++;
        
        if (checkGoogleMaps()) {
          if (intervalId) clearInterval(intervalId);
        } else if (attempts >= maxAttempts) {
          if (intervalId) clearInterval(intervalId);
          console.error('❌ Google Maps no se inicializó correctamente después de 2 segundos');
          setLoadError('Google Maps no se inicializó correctamente');
        }
      }, 100);
    };

    script.onerror = (error) => {
      console.error('❌ Error al cargar Google Maps API:', error);
      console.error('🔍 Posibles causas:');
      console.error('   - API key bloqueada o con restricciones');
      console.error('   - Dominio no autorizado en restricciones');
      console.error('   - APIs no habilitadas (Maps JavaScript API, Places API)');
      setLoadError('Error al cargar Google Maps API. Verifica la configuración de la API key.');
    };

    document.head.appendChild(script);

    // Cleanup
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  return { isLoaded, loadError };
};
